import { useState } from "react";
import moment from "moment";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEstimateChatsQuery, useIsInternalChatView } from "@/hooks/queries/estimate-chat";
import { useGetEstimateDetailQuery } from "@/hooks/queries/estimates/useGetEstimateDetailQuery";
import { useUser } from "@/hooks/useUser";
import type { EstimateChatSummary } from "@rebuild/shared/models";
import { ChatActionsMenu } from "./ChatActionsMenu";
import { ChatThread } from "./ChatThread";
import { ChatThreadSkeleton } from "./ChatThreadSkeleton";

interface AgentPanelProps {
  estimateId: string;
}

/**
 * Matched to the tab strip above: `size="sm"` outline buttons (h-7 / rounded-md /
 * px-2.5 / text-[0.8rem]). SelectTrigger ships taller with a shadow, so those are
 * overridden rather than left to drift from the tabs they sit beneath. (From the
 * design mockup's AgentSessionBar.)
 */
const CONTROL = "h-7 rounded-md border-border bg-background px-2.5 text-[0.8rem] shadow-none hover:bg-muted";

// Falls back to creator + timestamp until the chat is given a name.
const chatLabel = (chat: EstimateChatSummary) => chat.name || `${chat.userName} ${moment(chat.createdAt).format("MM/DD h:mm A")}`;

export function AgentPanel({ estimateId }: AgentPanelProps) {
  const { currentUser } = useUser();
  // Chats are read from the estimate's org, which is another org's when an internal user is viewing it.
  const organizationId = useGetEstimateDetailQuery(estimateId).data?.organizationId;
  const isInternalView = useIsInternalChatView(organizationId);
  const chatsQuery = useEstimateChatsQuery(estimateId, organizationId);
  const chats = chatsQuery.data ?? [];

  // A chat belongs to the user who created it; only the owner may add messages.
  const isOwnChat = (chat: { createdBy: string }) => chat.createdBy === currentUser?.lassoUserId;

  // undefined until the user picks a chat; until then the panel shows their most recent one. null is a
  // new draft from "+", which is only created when its first message is sent. Derived rather than set
  // once on load, so a later refetch can't pull the user out of a chat or draft they chose.
  const [chosenChatId, setChosenChatId] = useState<string | null>();
  // An internal viewer has no chats of their own here, so they start on the newest one.
  const defaultChatId = isInternalView ? chats[0]?.chatId : chats.find(isOwnChat)?.chatId;
  const selectedChatId = chosenChatId === undefined ? defaultChatId : (chosenChatId ?? undefined);
  const selectedChat = chats.find((c) => c.chatId === selectedChatId);
  const isReadOnly = isInternalView || (selectedChat ? !isOwnChat(selectedChat) : false);

  // Ownership needs currentUser, so wait for it too or the user's own chat would open as a draft.
  // On error, fall through to the empty state rather than loading forever.
  const isLoadingChats = chatsQuery.isPending || !currentUser;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-1 border-b border-border px-4 py-2">
        {isLoadingChats ? (
          <Skeleton className="h-7 min-w-0 flex-1" />
        ) : chats.length === 0 ? (
          <span className="flex h-7 min-w-0 flex-1 items-center px-2.5 text-[0.8rem] text-muted-foreground">No chats yet</span>
        ) : (
          <Select value={selectedChatId ?? ""} onValueChange={setChosenChatId}>
            <SelectTrigger className={`${CONTROL} min-w-0 flex-1 focus:ring-0 [&>svg]:size-3.5 [&>svg]:opacity-100`}>
              <SelectValue placeholder="New chat" />
            </SelectTrigger>
            <SelectContent>
              {chats.map((c) => (
                <SelectItem key={c.chatId} value={c.chatId} className="text-[0.8rem]">
                  {chatLabel(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {selectedChat && isOwnChat(selectedChat) && (
          <ChatActionsMenu
            estimateId={estimateId}
            chat={selectedChat}
            triggerClassName={`${CONTROL} w-7 px-0`}
            // Archiving the shown chat opens a draft. An undefined choice means the shown chat was the
            // default one, which is the one just archived.
            onArchived={(archivedId) => setChosenChatId((current) => (current === archivedId || current === undefined ? null : current))}
          />
        )}

        {!isInternalView && (
          <Button
            variant="outline"
            size="sm"
            className={`${CONTROL} w-7 px-0`}
            // "+" opens a fresh draft; nothing is created until it gets a message.
            onClick={() => setChosenChatId(null)}
            disabled={!selectedChatId}
            aria-label="New chat"
          >
            <Plus className="size-3.5" />
          </Button>
        )}
      </div>

      {isLoadingChats ? (
        <ChatThreadSkeleton />
      ) : (
        <ChatThread estimateId={estimateId} organizationId={organizationId} chatId={selectedChatId} readOnly={isReadOnly} onChatCreated={setChosenChatId} />
      )}
    </div>
  );
}
